import { useCallback, useEffect, useMemo, useState } from "react";
import AdminPageHeader from "../../components/admin/AdminPageHeader";
import RefreshButton from "../../components/admin/RefreshButton";
import { useAdminToast } from "../../context/AdminToastContext";
import { parseApiError } from "../../lib/http/apiErrors";
import {
  fetchPlansMatrix,
  setConfigValue,
  syncAllPlans,
  type PlanCell,
  type PlanSyncResult,
  type PlansMatrix,
} from "../../features/admin/billingApi";

/** Billing → Plans & Prices: every price customers are charged, in one table.
 *
 *  Saving a price creates its Cashfree plan right away (backend plan_sync), so
 *  a gateway problem shows up here — not at a customer's checkout. Gateway
 *  plans cannot change amount: a new price is a new plan, and subscribers
 *  already on the old one keep their price. */

const COLUMNS: { cycle: PlanCell["cycle"]; currency: PlanCell["currency"]; label: string; unit: string }[] = [
  { cycle: "monthly", currency: "INR", label: "Monthly", unit: "₹" },
  { cycle: "yearly", currency: "INR", label: "Yearly", unit: "₹" },
  { cycle: "monthly", currency: "USD", label: "Monthly", unit: "$" },
  { cycle: "yearly", currency: "USD", label: "Yearly", unit: "$" },
];

const TIER_LABEL: Record<string, string> = {
  starter: "Starter", mid: "Mid", premium: "Premium", pro: "Pro", studio: "Studio", elite: "Elite",
};

const STATUS_STYLE: Record<PlanCell["status"], { dot: string; text: string }> = {
  ready: { dot: "bg-emerald-400", text: "Cashfree plan ready" },
  missing: { dot: "bg-amber-400", text: "No Cashfree plan yet — Save or Sync creates it" },
  unpriced: { dot: "bg-gray-600", text: "Not offered (price 0)" },
};

function cellId(role: string, tier: string, c: { cycle: string; currency: string }) {
  return `${role}:${tier}:${c.cycle}:${c.currency}`;
}

function Toggle({
  checked, onChange, disabled,
}: { checked: boolean; onChange: (v: boolean) => void; disabled?: boolean }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors disabled:opacity-50 ${
        checked ? "bg-indigo-600" : "bg-gray-700"
      }`}
    >
      <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${
        checked ? "translate-x-5" : "translate-x-0.5"
      }`} />
    </button>
  );
}

function SyncReport({ result, onClose }: { result: PlanSyncResult; onClose: () => void }) {
  const errors = result.errors ?? [];
  const plans = result.plans ?? [];
  return (
    <div className={`mb-6 rounded-xl border p-4 text-sm ${
      errors.length ? "border-red-800 bg-red-950/30" : "border-emerald-800 bg-emerald-950/20"
    }`}>
      <div className="flex items-start justify-between gap-3">
        <p className={errors.length ? "text-red-300" : "text-emerald-300"}>
          {result.skipped
            ? `Skipped: ${result.skipped}`
            : `${plans.length} Cashfree plan(s) ready${errors.length ? `, ${errors.length} failed` : ""}.`}
        </p>
        <button onClick={onClose} className="text-gray-500 hover:text-white" aria-label="Dismiss">✕</button>
      </div>
      {errors.length > 0 && (
        <ul className="mt-2 space-y-1 text-xs text-red-300">
          {errors.map((e, i) => (
            <li key={i}>
              <span className="font-mono">{[e.plan_key, e.cycle, e.currency].filter(Boolean).join(" · ")}</span>
              {" — "}{e.error}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function AdminPlansPricesPage() {
  const { showError, showSuccess } = useAdminToast();
  const [matrix, setMatrix] = useState<PlansMatrix | null>(null);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [report, setReport] = useState<PlanSyncResult | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setMatrix(await fetchPlansMatrix());
      setDraft({});
    } catch (err) {
      showError(parseApiError(err).message);
    } finally {
      setLoading(false);
    }
  }, [showError]);

  useEffect(() => { void load(); }, [load]);

  const cellsById = useMemo(() => {
    const m = new Map<string, { role: string; tier: string; cell: PlanCell }>();
    matrix?.rows.forEach((r) => r.cells.forEach((c) => m.set(cellId(r.role, r.tier, c), { role: r.role, tier: r.tier, cell: c })));
    return m;
  }, [matrix]);

  const dirty = Object.entries(draft).filter(([id, v]) => {
    const cur = cellsById.get(id)?.cell.price;
    return v.trim() !== "" && Number(v) !== cur;
  });

  const saveAll = async () => {
    const bad = dirty.find(([, v]) => !/^\d+$/.test(v.trim()));
    if (bad) {
      showError("Prices must be whole numbers (0 = not offered).");
      return;
    }
    setSaving(true);
    const combined: PlanSyncResult = { plans: [], errors: [] };
    try {
      for (const [id, v] of dirty) {
        const entry = cellsById.get(id);
        if (!entry) continue;
        const res = await setConfigValue(entry.cell.key, Number(v.trim()));
        combined.plans!.push(...(res.plan_sync?.plans ?? []));
        combined.errors!.push(...(res.plan_sync?.errors ?? []));
        if (res.plan_sync?.skipped) combined.skipped = res.plan_sync.skipped;
      }
      // One key re-syncs its whole role, so the same plan (or failure) can
      // come back once per saved key; list each once.
      const seenPlans = new Set<string>();
      combined.plans = combined.plans!.filter((p) => !seenPlans.has(p.plan_id) && !!seenPlans.add(p.plan_id));
      const seenErrors = new Set<string>();
      combined.errors = combined.errors!.filter((e) => {
        const k = `${e.plan_key}|${e.cycle}|${e.currency}`;
        return !seenErrors.has(k) && !!seenErrors.add(k);
      });
      setReport(combined);
      if (!combined.errors!.length) showSuccess(`${dirty.length} price(s) saved.`);
      await load();
    } catch (err) {
      showError(parseApiError(err).message);
    } finally {
      setSaving(false);
    }
  };

  const runSync = async () => {
    setSyncing(true);
    try {
      setReport(await syncAllPlans());
      await load();
    } catch (err) {
      showError(parseApiError(err).message);
    } finally {
      setSyncing(false);
    }
  };

  const setFlag = async (key: keyof PlansMatrix["flags"], on: boolean) => {
    try {
      await setConfigValue(key, on ? 1 : 0);
      showSuccess(`${on ? "Enabled" : "Disabled"}.`);
      await load();
    } catch (err) {
      showError(parseApiError(err).message);
    }
  };

  const savingsHint = (role: string, tier: string, currency: string) => {
    const m = cellsById.get(cellId(role, tier, { cycle: "monthly", currency }))?.cell.price ?? 0;
    const y = cellsById.get(cellId(role, tier, { cycle: "yearly", currency }))?.cell.price ?? 0;
    if (m <= 0 || y <= 0) return null;
    const pct = Math.round(((m * 12 - y) / (m * 12)) * 100);
    if (pct <= 0) return <span className="text-amber-400">no saving vs 12× monthly</span>;
    if (pct > 60) return <span className="text-amber-400">save {pct}% — is this a monthly figure?</span>;
    return <span className="text-emerald-400">save {pct}%</span>;
  };

  const flags = matrix?.flags;
  const gw = matrix?.gateway;

  return (
    <div>
      <AdminPageHeader
        title="Plans & Prices"
        subtitle="Every price customers are charged. Saving a price creates its Cashfree plan immediately."
        actions={
          <>
            <RefreshButton onRefresh={load} />
            <button
              onClick={() => void runSync()}
              disabled={syncing || !gw?.cashfree_enabled}
              className="rounded-lg border border-gray-700 px-3 py-2 text-sm text-gray-200 hover:bg-gray-800 disabled:opacity-50"
              title="Create any missing Cashfree plan for the current prices. Safe to run any time."
            >
              {syncing ? "Syncing…" : "Sync all plans"}
            </button>
          </>
        }
      />

      {report && <SyncReport result={report} onClose={() => setReport(null)} />}

      {/* Gateway + switches */}
      <div className="mb-6 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <div className="rounded-xl border border-gray-800 bg-gray-900 p-4">
          <p className="text-xs text-gray-500">Payment gateway</p>
          <p className="mt-1 text-sm text-white">
            Cashfree{" "}
            {gw ? (
              <span className={`ml-1 rounded px-1.5 py-0.5 text-xs font-medium ${
                gw.mode === "production" ? "bg-red-900/40 text-red-300" : "bg-amber-900/40 text-amber-300"
              }`}>
                {gw.mode === "production" ? "LIVE" : "SANDBOX"}
              </span>
            ) : null}
          </p>
          {gw && !gw.cashfree_enabled && (
            <p className="mt-2 text-xs text-red-400">Cashfree is not enabled — plans cannot be created. See Payment Gateway.</p>
          )}
        </div>
        <div className="rounded-xl border border-gray-800 bg-gray-900 p-4">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-sm text-white">Yearly billing</p>
              <p className="text-xs text-gray-500">Show the Monthly / Yearly toggle to customers</p>
            </div>
            <Toggle checked={!!flags?.YEARLY_PLANS_ENABLED} disabled={!flags}
              onChange={(v) => void setFlag("YEARLY_PLANS_ENABLED", v)} />
          </div>
        </div>
        <div className="rounded-xl border border-gray-800 bg-gray-900 p-4">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-sm text-white">Automatic Cashfree plans (monthly)</p>
              <p className="text-xs text-gray-500">Off = monthly checkout uses the legacy hand-entered plan IDs</p>
            </div>
            <Toggle checked={!!flags?.PLAN_CATALOG_ENABLED} disabled={!flags}
              onChange={(v) => void setFlag("PLAN_CATALOG_ENABLED", v)} />
          </div>
        </div>
        <div className="rounded-xl border border-gray-800 bg-gray-900 p-4">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-sm text-white">Prices exclude GST</p>
              <p className="text-xs text-gray-500">
                On = ₹ prices are before GST and 18% is added at checkout (“₹1,999 + GST”). Off = prices include GST.
                Applies to new subscriptions; existing ones keep their amount.
              </p>
            </div>
            <Toggle checked={!!flags?.PRICES_EXCLUDE_GST} disabled={!flags}
              onChange={(v) => void setFlag("PRICES_EXCLUDE_GST", v)} />
          </div>
        </div>
      </div>

      <div className="mb-4 rounded-xl border border-gray-800 bg-gray-900/60 p-3 text-xs text-gray-400">
        Prices are whole units: rupees for ₹, dollars for $. <b className="text-gray-300">0 = not offered.</b>{" "}
        Customers outside India are billed in ₹ by card while BILLING_FOREIGN_CHARGE_CURRENCY=INR (shown in their own
        currency with “billed as ₹X”); $ prices are used only when it is USD.
        A price change creates a <b className="text-gray-300">new</b> Cashfree plan — subscribers on the old price keep it.
      </div>

      {loading && !matrix ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : (
        (["seller", "buyer"] as const).map((role) => (
          <div key={role} className="mb-6 overflow-x-auto rounded-xl border border-gray-800 bg-gray-900">
            <table className="w-full min-w-[720px] text-sm">
              <thead>
                <tr className="border-b border-gray-800 text-left text-xs text-gray-500">
                  <th className="px-4 py-3 font-medium text-white">{role === "seller" ? "Seller plans" : "Buyer plans"}</th>
                  {COLUMNS.map((c) => (
                    <th key={`${c.cycle}-${c.currency}`} className="px-3 py-3 font-medium">
                      {c.label} <span className="text-gray-400">({c.unit} {c.currency})</span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-800">
                {matrix?.rows.filter((r) => r.role === role).map((r) => (
                  <tr key={r.tier}>
                    <td className="px-4 py-3 font-medium text-gray-200">{TIER_LABEL[r.tier] ?? r.tier}</td>
                    {COLUMNS.map((col) => {
                      const id = cellId(r.role, r.tier, col);
                      const cell = cellsById.get(id)?.cell;
                      if (!cell) return <td key={id} />;
                      const value = draft[id] ?? String(cell.price);
                      const changed = draft[id] !== undefined && Number(draft[id]) !== cell.price;
                      const st = STATUS_STYLE[cell.status];
                      return (
                        <td key={id} className="px-3 py-2 align-top">
                          <div className="flex items-center gap-2">
                            <span className="text-gray-500">{col.unit}</span>
                            <input
                              inputMode="numeric"
                              value={value}
                              onChange={(e) => setDraft((d) => ({ ...d, [id]: e.target.value }))}
                              className={`w-24 rounded-lg border bg-gray-800 px-2 py-1.5 text-white outline-none focus:ring-2 focus:ring-indigo-500 ${
                                changed ? "border-amber-500" : "border-gray-700"
                              }`}
                              aria-label={`${r.role} ${r.tier} ${col.cycle} ${col.currency}`}
                            />
                            <span
                              className={`h-2.5 w-2.5 shrink-0 rounded-full ${st.dot}`}
                              title={`${st.text}${cell.plan_id ? `\n${cell.plan_name ?? ""}\n${cell.plan_id}` : ""}`}
                            />
                          </div>
                          <div className="mt-1 min-h-[1rem] text-[11px]">
                            {col.cycle === "yearly" ? savingsHint(r.role, r.tier, col.currency) : null}
                            {cell.status === "ready" && col.cycle === "monthly" && (
                              <span className="text-gray-600" title={cell.plan_id ?? ""}>plan ready</span>
                            )}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))
      )}

      <div className="sticky bottom-0 flex items-center justify-end gap-3 border-t border-gray-800 bg-gray-950/90 py-3 backdrop-blur">
        {dirty.length > 0 && <span className="text-sm text-amber-300">{dirty.length} unsaved change(s)</span>}
        <button
          onClick={() => setDraft({})}
          disabled={!dirty.length || saving}
          className="rounded-lg px-4 py-2 text-sm text-gray-400 hover:text-white disabled:opacity-40"
        >
          Discard
        </button>
        <button
          onClick={() => void saveAll()}
          disabled={!dirty.length || saving}
          className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-500 disabled:opacity-40"
        >
          {saving ? "Saving & creating plans…" : "Save prices"}
        </button>
      </div>

      <p className="mt-4 text-xs text-gray-600">
        Legend: <span className="text-emerald-400">●</span> Cashfree plan ready ·{" "}
        <span className="text-amber-400">●</span> priced, no plan yet ·{" "}
        <span className="text-gray-500">●</span> not offered. Hover a dot for the plan name and ID.
      </p>
    </div>
  );
}
